import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './OrgasmeatAccountPage.css';

const EMPTY_PROFILE = { firstName: '', lastName: '', birthDate: '', email: '', consent: false };
const EXPIRED = 'Ce lien est invalide ou a expiré. Demande un nouveau lien depuis la borne.';
const localDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

function errorMessage(error) {
  if (error.code === 'DEMO_IDENTITY_LINK_EXPIRED' || error.status === 401) return EXPIRED;
  if (error.code === 'DEMO_IDENTITY_LINK_PENDING') return 'Ton lien se prépare encore. Réessaie dans quelques instants.';
  if (error.status === 429) return 'Trop de tentatives pour le moment. Patiente un peu avant de réessayer.';
  if (error.code === 'DEMO_IDENTITY_CONSENT_REQUIRED') return 'Ton accord est nécessaire pour continuer.';
  if (error.code === 'DEMO_IDENTITY_INPUT_INVALID') return 'Vérifie les informations du formulaire puis réessaie.';
  if (error.code === 'TIMEOUT') return 'La réponse prend trop de temps. Réessaie dans un instant.';
  return 'La connexion est momentanément indisponible. Vérifie ton réseau puis réessaie.';
}

async function postLink(action, body, controller) {
  let timeout;
  let abortHandler;
  let timedOut = false;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(`/api/orgasmeat-demo/identity/link/${action}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
          credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal,
        });
        const value = await response.json();
        if (!response.ok) throw Object.assign(new Error('link-request-failed'), { code: value?.code, status: response.status });
        return value;
      })(),
      new Promise((_, reject) => {
        abortHandler = () => reject(Object.assign(new Error('request-aborted'), { code: timedOut ? 'TIMEOUT' : 'ABORTED' }));
        controller.signal.addEventListener('abort', abortHandler, { once: true });
        timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
      }),
    ]);
  } finally { clearTimeout(timeout); controller.signal.removeEventListener('abort', abortHandler); }
}

function validInfo(value) {
  const remaining = Date.parse(value?.expiresAt) - Date.now();
  return value && ['login', 'register'].includes(value.mode) && typeof value.needsProfile === 'boolean'
    && typeof value.maskedPhone === 'string' && value.maskedPhone.length > 0 && value.maskedPhone.length <= 40
    && /[*•…]/.test(value.maskedPhone) && !Array.from(value.maskedPhone).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)
    && typeof value.expiresAt === 'string' && remaining > 0 && remaining <= 605000;
}

function profileError(profile, needsProfile) {
  if (needsProfile) {
    if (!profile.firstName.trim() || profile.firstName.trim().length > 60) return 'Indique ton prénom (60 caractères maximum).';
    if (profile.lastName.trim().length > 80) return 'Ton nom doit contenir au maximum 80 caractères.';
    if (profile.email && (profile.email.trim().length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email.trim()))) return 'Indique une adresse e-mail valide ou laisse ce champ vide.';
    if (profile.birthDate) {
      const date = new Date(`${profile.birthDate}T00:00:00Z`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(profile.birthDate) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== profile.birthDate || profile.birthDate > localDate()) return 'Choisis une date de naissance valide, qui ne soit pas dans le futur.';
    }
  }
  if (!profile.consent) return 'Coche ton accord pour utiliser ces informations avec ton compte fidélité.';
  return '';
}

function AccountLink({ token }) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ phase: 'loading', info: null, error: '', kioskLinked: false });
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const alive = useRef(false);
  const request = useRef(null);
  const expiry = useRef(null);
  const submitting = useRef(false);
  const current = useCallback(controller => alive.current && request.current === controller, []);
  const expire = useCallback(() => {
    request.current?.abort(); request.current = null;
    submitting.current = false;
    setProfile(EMPTY_PROFILE);
    setState({ phase: 'expired', info: null, error: EXPIRED, kioskLinked: false });
  }, []);

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController(); request.current = controller;
    setProfile(EMPTY_PROFILE);
    setState({ phase: 'loading', info: null, error: '', kioskLinked: false });
    if (!token) setState({ phase: 'expired', info: null, error: EXPIRED, kioskLinked: false });
    else (async () => {
      try {
        const value = await postLink('info', { token }, controller);
        if (!current(controller)) return;
        if (Date.parse(value?.expiresAt) <= Date.now()) { expire(); return; }
        if (!validInfo(value)) throw new Error('invalid-info');
        const info = { mode: value.mode, needsProfile: value.needsProfile, maskedPhone: value.maskedPhone, expiresAt: value.expiresAt };
        setState({ phase: 'ready', info, error: '', kioskLinked: false });
        expiry.current = setTimeout(expire, Date.parse(info.expiresAt) - Date.now());
      } catch (error) {
        if (current(controller)) setState({ phase: error.code === 'DEMO_IDENTITY_LINK_EXPIRED' || error.status === 401 ? 'expired' : 'error', info: null, error: errorMessage(error), kioskLinked: false });
      }
    })();
    return () => { alive.current = false; request.current?.abort(); request.current = null; clearTimeout(expiry.current); };
  }, [token, attempt, current, expire]);

  async function complete(event) {
    event.preventDefault();
    if (submitting.current || state.phase !== 'ready' || !state.info) return;
    if (Date.parse(state.info.expiresAt) <= Date.now()) { expire(); return; }
    const invalid = profileError(profile, state.info.needsProfile);
    if (invalid) { setState(value => ({ ...value, error: invalid })); return; }
    const controller = new AbortController(); request.current?.abort(); request.current = controller;
    submitting.current = true;
    setState(value => ({ ...value, phase: 'submitting', error: '' }));
    const body = { token, consent: true };
    if (state.info.needsProfile) {
      body.firstName = profile.firstName.trim();
      for (const key of ['lastName', 'birthDate', 'email']) if (profile[key].trim()) body[key] = profile[key].trim();
    }
    try {
      const value = await postLink('complete', body, controller);
      if (!current(controller)) return;
      if (value?.completed !== true || typeof value.kioskLinked !== 'boolean') throw new Error('invalid-completion');
      clearTimeout(expiry.current);
      setProfile(EMPTY_PROFILE);
      setState({ phase: 'success', info: null, error: '', kioskLinked: value.kioskLinked });
    } catch (error) {
      if (!current(controller)) return;
      if (error.code === 'DEMO_IDENTITY_LINK_EXPIRED' || error.status === 401) expire();
      else setState(value => ({ ...value, phase: 'ready', error: errorMessage(error) }));
    } finally { if (current(controller)) submitting.current = false; }
  }

  const busy = state.phase === 'submitting';
  const fields = state.info?.needsProfile;
  const change = event => setProfile(value => ({ ...value, [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const title = state.phase === 'success' ? 'Ton compte est prêt.' : state.phase === 'expired' ? 'Un nouveau lien, et c’est reparti.'
    : state.info ? fields ? 'Faisons connaissance.' : 'Ravi de te retrouver.' : 'Ton espace ORGASMEAT.';
  return <main className="om-account-page">
    <header className="om-account-brand"><img src="/orgasmeat/logo.svg" alt="ORGASMEAT" /><span>Compte de démonstration BEKIOSK · Points d’essai</span></header>
    <section className="om-account-card" aria-labelledby="om-account-title" aria-busy={state.phase === 'loading' || busy}>
      <p className="om-account-eyebrow">Plus de plaisir, à chaque visite.</p>
      <h1 id="om-account-title">{title}</h1>
      {state.phase === 'loading' && <p role="status">Vérification de ton lien privé…</p>}
      {state.error && <p className="om-account-error" role="alert">{state.error}</p>}
      {state.phase === 'error' && <button className="om-account-button" type="button" onClick={() => setAttempt(value => value + 1)}>Réessayer</button>}
      {state.info && <form onSubmit={complete} noValidate>
        <p className="om-account-phone">Ton numéro <strong>{state.info.maskedPhone}</strong></p>
        <p>{fields ? 'Un prénom suffit pour commencer. Les autres informations sont facultatives.' : 'Confirme ton accord pour retrouver ton compte sur la borne.'}</p>
        <fieldset disabled={busy}>
          {fields && <div className="om-account-fields">
            <label>Prénom <span>obligatoire</span><input name="firstName" autoComplete="given-name" maxLength={60} required value={profile.firstName} onChange={change} /></label>
            <label>Nom <span>facultatif</span><input name="lastName" autoComplete="family-name" maxLength={80} value={profile.lastName} onChange={change} /></label>
            <label>Date de naissance <span>facultative</span><input name="birthDate" type="date" autoComplete="bday" max={localDate()} value={profile.birthDate} onChange={change} /></label>
            <label>E-mail <span>facultatif</span><input name="email" type="email" inputMode="email" autoComplete="email" maxLength={254} value={profile.email} onChange={change} /></label>
          </div>}
          <label className="om-account-consent"><input name="consent" type="checkbox" required checked={profile.consent} onChange={change} /><span>J’accepte l’utilisation de mon numéro et des informations fournies pour mon compte fidélité de démonstration ORGASMEAT, selon la <a href="/confidentialite.html" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">politique de confidentialité</a>.</span></label>
          <button className="om-account-button" type="submit">{busy ? 'Confirmation en cours…' : fields ? 'Créer mon compte' : 'Confirmer ma connexion'}</button>
        </fieldset>
        <p className="om-account-note">Lien privé valable 10 minutes. Cet accord ne t’inscrit à aucune communication commerciale.</p>
      </form>}
      {state.phase === 'success' && <div className="om-account-success" role="status"><span aria-hidden="true">✓</span><p>{state.kioskLinked ? 'Retourne à la borne : ta connexion est confirmée. Tu peux continuer ta commande.' : 'Le lien avec la borne n’est plus actif. Retourne à la borne et connecte-toi à nouveau pour continuer.'}</p></div>}
      {['success', 'expired'].includes(state.phase) && <a className="om-account-return" href="/orgasmeat/" referrerPolicy="no-referrer">Découvrir l’expérience ORGASMEAT</a>}
    </section>
    <footer className="om-account-footer">Ton plaisir. Ton compte. En toute simplicité.</footer>
  </main>;
}

export default function OrgasmeatAccountPage() {
  const location = useLocation();
  const match = /^#token=([A-Za-z0-9_-]{43})$/.exec(location.hash);
  const token = !location.search && match ? match[1] : null;
  return <AccountLink key={`${location.hash}/${location.search}`} token={token} />;
}
